<template>
    <div class="reserved-retreat-report">
        <div class="card shadow-sm border-0">

            <HeaderFilter
                title="Reservados X Retirados"
                @apply-filters="loadData"
            />

            <div class="card-body">
                <!-- Loading -->
                <div
                    v-if="loadingData"
                    class="text-center py-4"
                >
                    <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
                    <p class="mt-2">Carregando dados...</p>
                </div>

                <!-- Conteúdo Principal -->
                <div v-else>
                    <!-- Métricas Resumidas -->
                    <div class="metrics-summary mb-4">
                        <div class="row">
                            <div class="col-md-3">
                                <div class="metric-card">
                                    <div class="metric-value">{{ totalReserved }}</div>
                                    <div class="metric-label">Total Reservados</div>
                                    <div class="metric-icon reserved">
                                        <i class="fas fa-calendar-plus"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="metric-card">
                                    <div class="metric-value">{{ totalRetrieved }}</div>
                                    <div class="metric-label">Total Retirados</div>
                                    <div class="metric-icon retrieved">
                                        <i class="fas fa-car"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="metric-card">
                                    <div class="metric-value">{{ commitmentRate }}%</div>
                                    <div class="metric-label">Taxa de Compromisso</div>
                                    <div class="metric-icon commitment">
                                        <i class="fas fa-handshake"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="metric-card">
                                    <div class="metric-value">{{ pendingReservations }}</div>
                                    <div class="metric-label">Pendentes</div>
                                    <div class="metric-icon pending">
                                        <i class="fas fa-clock"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Gráfico -->
                    <div class="chart-container mb-4">
                        <ChartBar
                            v-if="renderChart && chartData.labels.length > 0"
                            :chart-data="chartData"
                            :options="chartOptions"
                            :height="300"
                        />
                        <div
                            v-else
                            class="empty-chart"
                        >
                            <i class="fas fa-chart-bar fa-3x text-muted"></i>
                            <p class="text-muted mt-2">Nenhum dado disponível para o período selecionado</p>
                        </div>
                    </div>

                    <!-- Tabela de Dados -->
                    <div class="data-table">
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead class="bg-light">
                                <tr>
                                    <th>Mês</th>
                                    <th class="text-center">Reservados</th>
                                    <th class="text-center">Retirados</th>
                                    <th class="text-center">% Compromisso</th>
                                    <th class="text-center">Diferença</th>
                                    <th class="text-center">Tendência</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="(data, index) in tableData"
                                    :key="index"
                                >
                                    <td class="font-weight-bold">{{ data.monthName }}</td>
                                    <td class="text-center">
                                        <span class="badge badge-info">{{ data.reserveds }}</span>
                                    </td>
                                    <td class="text-center">
                                        <span class="badge badge-success">{{ data.devoluteds }}</span>
                                    </td>
                                    <td class="text-center">
                                        <span
                                            class="badge"
                                            :class="getCommitmentBadgeClass(data.percent)"
                                        >
                                            {{ data.percent }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <span
                                            class="badge"
                                            :class="getDifferenceBadgeClass(data.difference)"
                                        >
                                            {{ data.difference > 0 ? '+' : '' }}{{ data.difference }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <i
                                            class="fas"
                                            :class="getTrendIcon(data, index)"
                                            :style="getTrendColor(data, index)"
                                        ></i>
                                    </td>
                                </tr>
                                <tr v-if="tableData.length === 0">
                                    <td
                                        colspan="6"
                                        class="text-center text-muted py-4"
                                    >
                                        Nenhum dado encontrado para o período selecionado
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Análise Adicional -->
                    <div class="analysis-section mt-4">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="analysis-card">
                                    <h6 class="analysis-title">
                                        <i class="fas fa-chart-line mr-2"></i>
                                        Tendência Mensal
                                    </h6>
                                    <div class="analysis-content">
                                        <p class="mb-2">
                                            <strong>Melhor Mês:</strong>
                                            <span class="text-success">{{ bestMonth.monthName || 'N/A' }}</span>
                                            <span
                                                v-if="bestMonth.percent"
                                                class="text-muted"
                                            >
                                                ({{ bestMonth.percent }})
                                            </span>
                                        </p>
                                        <p class="mb-0">
                                            <strong>Pior Mês:</strong>
                                            <span class="text-danger">{{ worstMonth.monthName || 'N/A' }}</span>
                                            <span
                                                v-if="worstMonth.percent"
                                                class="text-muted"
                                            >
                                                ({{ worstMonth.percent }})
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="analysis-card">
                                    <h6 class="analysis-title">
                                        <i class="fas fa-info-circle mr-2"></i>
                                        Resumo do Período
                                    </h6>
                                    <div class="analysis-content">
                                        <p class="mb-2">
                                            <strong>Média de Compromisso:</strong>
                                            <span :class="commitmentRate >= 80 ? 'text-success' : commitmentRate >= 60 ? 'text-warning' : 'text-danger'">
                                                {{ avgCommitment }}%
                                            </span>
                                        </p>
                                        <p class="mb-0">
                                            <strong>Total de Cancelamentos:</strong>
                                            <span class="text-info">{{ totalCancellations }}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ChartBar from "@/components/Utils/ChartBar.js";
import vSelect from "vue-select";
import {getMessage} from "../../composables/message";
import HeaderFilter from "./HeaderFilter.vue";

export default {
    name: "ReservedRetreatReport", // nome do componente

    components: {
        HeaderFilter,
        ChartBar, // componente de gráfico de barras
        vSelect, // componente de select
    },

    data() {
        return {
            loadingData: false, // controla carregamento dos dados
            renderChart: false, // controla renderização do gráfico

            // localFilters - filtros específicos deste relatório
            localFilters: {
                branch_id: null, // ID da filial
                retreat_date: "", // data de início
                devolution_date: "", // data final
            },

            // branches - lista de filiais
            branches: [],

            // chartData - dados para o gráfico
            chartData: {
                labels: [], // labels dos meses
                datasets: [
                    {
                        label: "Reservados",
                        backgroundColor: "#FFB22B",
                        borderColor: "#FFB22B",
                        borderWidth: 1,
                        data: [], // dados de reservados
                    },
                    {
                        label: "Retirados",
                        backgroundColor: "#1B9E77",
                        borderColor: "#1B9E77",
                        borderWidth: 1,
                        data: [], // dados de retirados
                    },
                ],
            },

            // chartOptions - opções de configuração do gráfico
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            padding: 20
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        borderWidth: 1,
                        callbacks: {
                            afterBody: function (context) {
                                const dataIndex = context[0].dataIndex;
                                const reserved = context[0].chart.data.datasets[0].data[dataIndex];
                                const retrieved = context[0].chart.data.datasets[1].data[dataIndex];
                                const rate = reserved > 0 ? Math.round((retrieved / reserved) * 100) : 0;
                                return `Taxa de Compromisso: ${rate}%`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: 'rgba(0, 0, 0, 0.1)'
                        }
                    },
                    x: {
                        grid: {
                            display: false
                        }
                    }
                }
            },

            // tableData - dados para a tabela
            tableData: [],
        };
    },

    computed: {
        /**
         * Total de reservados / Total reserved
         */
        totalReserved() {
            return this.tableData.reduce((sum, item) => sum + (item.reserveds || 0), 0);
        },

        /**
         * Total de retirados / Total retrieved
         */
        totalRetrieved() {
            return this.tableData.reduce((sum, item) => sum + (item.devoluteds || 0), 0);
        },

        /**
         * Taxa de compromisso geral / Overall commitment rate
         */
        commitmentRate() {
            if (this.totalReserved === 0) return 0;
            return Math.round((this.totalRetrieved / this.totalReserved) * 100);
        },

        /**
         * Reservas pendentes / Pending reservations
         */
        pendingReservations() {
            return this.totalReserved - this.totalRetrieved;
        },

        /**
         * Total de cancelamentos estimado / Estimated total cancellations
         */
        totalCancellations() {
            return Math.max(0, this.pendingReservations);
        },

        /**
         * Média de compromisso / Average commitment
         */
        avgCommitment() {
            if (this.tableData.length === 0) return 0;
            const sum = this.tableData.reduce((acc, item) => {
                const percent = parseFloat(item.percent.replace('%', '') || 0);
                return acc + percent;
            }, 0);
            return Math.round(sum / this.tableData.length);
        },

        /**
         * Melhor mês / Best month
         */
        bestMonth() {
            if (this.tableData.length === 0) return {};
            console.log(this.tableData)
            return this.tableData.reduce((best, current) => {
                const currentPercent = parseFloat(current.percent.replace('%', '') || 0);
                const bestPercent = parseFloat(best.percent.replace('%', '') || 0);
                return currentPercent > bestPercent ? current : best;
            }, this.tableData[0]);
        },

        /**
         * Pior mês / Worst month
         */
        worstMonth() {
            if (this.tableData.length === 0) return {};
            return this.tableData.reduce((worst, current) => {
                const currentPercent = parseFloat(current.percent.replace('%', '') || 0);
                const worstPercent = parseFloat(worst.percent.replace('%', '') || 100);
                return currentPercent < worstPercent ? current : worst;
            }, this.tableData[0]);
        },
    },

    mounted() {
        this.loadData(); // carrega dados iniciais
    },

    methods: {
        /**
         * Carrega dados do relatório / Load report data
         */
        async loadData(params) {
            this.loadingData = true;
            this.renderChart = false;

            try {
                const api = this.$store.state.api + "dashboardOne";

                const response = await this.$http.get(api, {params});

                this.processData(response.data);
                this.renderChart = true;

            } catch (error) {
                console.error('Erro ao carregar dados reservados/retirados:', error);
                const message = error.response.data.errors
                    ? getMessage(error.response.data.errors)
                    : error.response.data || 'Erro ao carregar dados';
                this.$message(null, message, "error");
            } finally {
                this.loadingData = false;
            }
        },

        /**
         * Processa dados recebidos da API / Process data received from API
         */
        processData(data) {
            // Atualiza dados da tabela / Update table data
            this.tableData = (data.table || []).map((item, index) => ({
                ...item,
                difference: (item.devoluteds || 0) - (item.reserveds || 0),
                percentNumeric: parseFloat(item.percent.replace('%', '') || 0)
            }));

            // Atualiza dados do gráfico / Update chart data
            this.chartData.labels = data.months || [];
            this.chartData.datasets[0].data = data.reserveds || [];
            this.chartData.datasets[1].data = data.devoluteds || [];
        },

        /**
         * Retorna classe CSS para badge de compromisso / Return CSS class for commitment badge
         */
        getCommitmentBadgeClass(percent) {
            const rate = parseFloat(percent.replace('%', '') || 0);
            if (rate >= 90) return 'badge-success';
            if (rate >= 70) return 'badge-warning';
            return 'badge-danger';
        },

        /**
         * Retorna classe CSS para badge de diferença / Return CSS class for difference badge
         */
        getDifferenceBadgeClass(difference) {
            if (difference > 0) return 'badge-success';
            if (difference < 0) return 'badge-danger';
            return 'badge-secondary';
        },

        /**
         * Retorna ícone de tendência / Return trend icon
         */
        getTrendIcon(data, index) {
            if (index === 0) return 'fa-minus';

            const current = data.percentNumeric || 0;
            const previous = this.tableData[index - 1].percentNumeric || 0;

            if (current > previous) return 'fa-arrow-up';
            if (current < previous) return 'fa-arrow-down';
            return 'fa-minus';
        },

        /**
         * Retorna cor da tendência / Return trend color
         */
        getTrendColor(data, index) {
            if (index === 0) return 'color: #6c757d';

            const current = data.percentNumeric || 0;
            const previous = this.tableData[index - 1].percentNumeric || 0;

            if (current > previous) return 'color: #28a745';
            if (current < previous) return 'color: #dc3545';
            return 'color: #6c757d';
        },
    },
};
</script>

<style scoped>
/* Estilo principal do relatório */
.reserved-retreat-report .card {
    border-radius: 12px;
    overflow: hidden;
}

.bg-gradient-primary {
    background: linear-gradient(135deg, #4e73df 0%, #224abe 100%);
}

.metric-icon.reserved {
    background: linear-gradient(135deg, #36b9cc 0%, #258391 100%);
}

.metric-icon.retrieved {
    background: linear-gradient(135deg, #1cc88a 0%, #13855c 100%);
}

.metric-icon.commitment {
    background: linear-gradient(135deg, #f6c23e 0%, #d4a934 100%);
}

.metric-icon.pending {
    background: linear-gradient(135deg, #e74a3b 0%, #c0392b 100%);
}

/* Container do gráfico */
.chart-container {
    position: relative;
    background: white;
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid #e3e6f0;
}

.empty-chart {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
}

/* Tabela de dados */
.data-table {
    background: white;
    border-radius: 8px;
    overflow: hidden;
}

.table {
    margin-bottom: 0;
}

.table thead th {
    border-top: none;
    font-weight: 600;
    color: #5a5c69;
    background-color: #f8f9fc;
}

.table tbody tr:hover {
    background-color: #f8f9fc;
}

/* Análise adicional */
.analysis-section {
    margin-top: 2rem;
}

.analysis-card {
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
    height: 100%;
}

.analysis-title {
    color: #2d3748;
    font-weight: 600;
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.analysis-content {
    color: #4a5568;
}

/* Badges */
.badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
}

/* Formulários */
.form-label {
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
}

.form-control {
    border-radius: 0.35rem;
    border: 1px solid #d1d3e2;
}

.form-control:focus {
    border-color: #4e73df;
    box-shadow: 0 0 0 0.2rem rgba(78, 115, 223, 0.25);
}

/* Responsividade */
@media (max-width: 768px) {
    .metric-card {
        margin-bottom: 1rem;
    }

    .chart-container {
        height: 250px;
    }

    .metric-value {
        font-size: 1.5rem;
    }

    .table-responsive {
        font-size: 0.875rem;
    }

    .analysis-card {
        margin-bottom: 1rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Indicators/reserved_retreat.vue